// Lab (lab/iphone-40-no-merge): vult de staalknoppen die snippets/staal-knop.liquid met
// `uitgesteld: true` rendert. Die knop staat direct in de HTML, maar zonder staal-variant,
// prijs, beeld en formaten: het opzoeken van de gekoppelde staalproducten kost per kaart
// servertijd. Na het laden halen we die gegevens in één verzoek per collectiepagina op
// (<collectie-URL>?view=staaldata, templates/collection.staaldata.liquid).
//
// De gevulde knop wordt vervangen door een kloon, zodat de MutationObserver van
// staalkiezer.js hem als nieuwe knop ziet en de gekozen-staat uit de winkelmand zet.
// Tikt iemand vóór het vullen, dan vullen we meteen en spelen de klik daarna opnieuw af.

const SELECTOR = 'button[data-staal-uitgesteld]';
/** @type {Map<string, Promise<Record<string, any>>>} */
const perPagina = new Map();

/** @param {HTMLElement} knop */
function paginaVan(knop) {
  return knop.closest('[data-page]')?.getAttribute('data-page') || '';
}

/** @param {string} pagina */
function haalPagina(pagina) {
  const url = new URL(location.href);
  url.hash = '';
  url.searchParams.delete('section_id');
  url.searchParams.set('view', 'staaldata');
  if (pagina) url.searchParams.set('page', pagina);
  const sleutel = url.toString();
  let verzoek = perPagina.get(sleutel);
  if (verzoek) return verzoek;
  verzoek = fetch(url, { credentials: 'same-origin' })
    .then((res) => (res.ok ? res.json() : {}))
    .catch(() => ({}));
  perPagina.set(sleutel, verzoek);
  return verzoek;
}

/** Losse terugval voor een kaart die niet in de paginadata zit (andere bron dan de collectie). */
/** @param {HTMLElement} knop */
async function haalLos(knop) {
  const pad = (knop.dataset.staalUrl || '').split('?')[0];
  if (!pad) return null;
  try {
    const res = await fetch(`${pad}?view=wishlist`, { credentials: 'same-origin' });
    const data = res.ok ? await res.json() : null;
    const s = data?.staal;
    if (!s) return null;
    return { id: s.variant_id, prijs: s.price, beeld: s.beeld, varianten: s.formaten?.length ? s.formaten : undefined };
  } catch {
    return null;
  }
}

/**
 * @param {HTMLElement} knop
 * @returns {Promise<HTMLElement | null>} de gevulde knop, of null als er geen staal is
 */
async function vul(knop) {
  const id = knop.dataset.staalUitgesteld || '';
  const data = await haalPagina(paginaVan(knop));
  const staal = id in data ? data[id] : await haalLos(knop);
  if (!knop.isConnected) return null;
  if (!staal) {
    knop.remove();
    return null;
  }
  const nieuw = /** @type {HTMLElement} */ (knop.cloneNode(true));
  delete nieuw.dataset.staalUitgesteld;
  nieuw.dataset.staalId = String(staal.id);
  nieuw.dataset.staalPrijs = String(staal.prijs);
  if (staal.beeld) nieuw.dataset.staalBeeld = staal.beeld;
  if (staal.varianten) nieuw.dataset.staalVarianten = JSON.stringify(staal.varianten);
  knop.replaceWith(nieuw);
  return nieuw;
}

/** @type {WeakMap<HTMLElement, Promise<HTMLElement | null>>} */
const bezig = new WeakMap();
/** @param {HTMLElement} knop */
function vulEenmaal(knop) {
  let p = bezig.get(knop);
  if (!p) {
    p = vul(knop);
    bezig.set(knop, p);
  }
  return p;
}

function vulAlles() {
  document.querySelectorAll(SELECTOR).forEach((knop) => vulEenmaal(/** @type {HTMLElement} */ (knop)));
}

// Klik vóór het vullen: tegenhouden, vullen, en dan op de gevulde knop opnieuw klikken.
window.addEventListener(
  'click',
  (event) => {
    const knop = /** @type {HTMLElement | null} */ (event.target instanceof Element ? event.target.closest(SELECTOR) : null);
    if (!knop) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    knop.setAttribute('aria-busy', 'true');
    vulEenmaal(knop).then((nieuw) => nieuw?.click());
  },
  true
);
// Aanraken of focussen = waarschijnlijk zo een klik: alvast ophalen.
for (const type of ['pointerdown', 'focusin']) {
  document.addEventListener(type, (event) => {
    const knop = event.target instanceof Element ? event.target.closest(SELECTOR) : null;
    if (knop) vulEenmaal(/** @type {HTMLElement} */ (knop));
  }, { passive: true });
}

const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 200));
let vulGepland = false;
function naLaden() {
  if (vulGepland) return;
  vulGepland = true;
  idle(() => {
    vulGepland = false;
    vulAlles();
  }, { timeout: 2000 });
}

// Nieuwe knoppen (filteren, sorteren, meer laden) vullen we ook, maar pas als de browser
// even niets te doen heeft: direct na een filterklik concurreerde dit verzoek met het nieuwe
// raster (SHO-11729). Een tik vóór die tijd vult de knop meteen (zie hierboven).
new MutationObserver((mutaties) => {
  if (mutaties.some((m) => Array.from(m.addedNodes).some((n) => n instanceof Element && (n.matches(SELECTOR) || n.querySelector(SELECTOR))))) {
    if (document.readyState === 'complete') naLaden();
  }
}).observe(document.documentElement, { childList: true, subtree: true });

if (document.readyState === 'complete') naLaden();
else window.addEventListener('load', naLaden, { once: true });
